import { API_ENDPOINTS } from "../config/CONSTANTS";
import { Logger } from "../../helpers";
import { TRACKERS } from "../config/trackingFlags"; // Centralized tracker control

/**
 * @fileoverview Manages server-side tracking for different endpoints within the application.
 * @module ServerTrackerManager
 */

/**
 * The `ServerTrackerManager` class is responsible for handling event tracking on the server-side.
 * It sends event data to various server endpoints, such as Meta and Pinterest, while ensuring
 * that specific data is included or excluded based on the endpoint.
 *
 * ### High-Level Logic:
 * - Initializes server-side tracking.
 * - Tracks events by sending them to the specified server endpoints.
 * - Excludes specific data fields (e.g., fbq, fbc) when the event is not sent to Meta.
 *
 * ### Data Flow:
 * - Event data is prepared by the `DataManager` and passed to `ServerTrackerManager`.
 * - Based on the server endpoint, certain data fields may be excluded before sending the event.
 * - The event is then sent to the specified server endpoints via the `sendEventToApi` method.
 *
 * ### Structure:
 * - The class manages server-side endpoints and the logic for excluding or including specific data fields.
 * - Provides a method to track events on multiple server endpoints simultaneously.
 *
 * @example
 * const analytics = new Analytics();
 * analytics.initialize();
 *
 * // Track an event on all server-side endpoints
 * analytics.track.server("PageView");
 *
 * // Track an event only on Meta
 * analytics.track.server("PageView", customData, ["Meta"]);
 *
 * // Track an event on both Meta and Pinterest
 * analytics.track.server("PageView", customData, ["Meta", "Pinterest"]);
 */
class ServerTrackerManager {
  constructor() {}

  /**
   * Initializes the server-side tracking manager.
   * @async
   * @method initialize
   * @memberof ServerTrackerManager
   */
  async initialize() {
    if (!TRACKERS.SERVER_TRACKING) {
      Logger.info("Server-side tracking initialization skipped by flag.");
      return;
    }

    Logger.log("Server-side tracking initialized");
  }

  /**
   * Tracks an event on the server-side, excluding certain data for specific endpoints.
   * @async
   * @method trackEvent
   * @memberof ServerTrackerManager
   * @param {string} eventName - The event name to track.
   * @param {Object} eventData - The event data to send.
   * @param {Array|string} [serverOptions="all"] - The server endpoints to send the event to.
   * @returns {Promise<void>} A promise that resolves when all tracking requests are completed.
   */
  async trackEvent(eventName, eventData, serverOptions = "all") {
    if (!TRACKERS.SERVER_TRACKING) {
      Logger.info(`🚫 Skipping server tracking for event: ${eventName}`);
      return;
    }

    Logger.log(`Tracking event: ${eventName}`);

    // Determine the server endpoints to send the event to
    const endpoints =
      serverOptions === "all"
        ? Object.values(API_ENDPOINTS)
        : serverOptions.map((opt) => API_ENDPOINTS[opt]);

    try {
      const promises = endpoints.map((endpoint) => {
        const payload = { ...eventData };

        // Only exclude fbq and fbc for non-Meta endpoints
        if (endpoint !== API_ENDPOINTS.Meta && endpoint !== API_ENDPOINTS.FacebookPixel) {
          if (payload.userData) {
            delete payload.userData.fbq;
            delete payload.userData.fbc;
          }
        }

        return this.sendEventToApi(endpoint, payload);
      });

      await Promise.allSettled(promises);

      Logger.log(`Server-side tracking completed for: ${eventName}`);
    } catch (error) {
      Logger.error(`Error in server-side tracking for: ${eventName}`, error);
    }
  }

  /**
   * Sends the event data to a specific server endpoint.
   * @async
   * @method sendEventToApi
   * @memberof ServerTrackerManager
   * @param {string} endpoint - The server endpoint URL.
   * @param {Object} payload - The event data payload to send.
   * @returns {Promise<void>} A promise that resolves when the event is successfully sent.
   */
  async sendEventToApi(endpoint, payload) {
    try {
      Logger.log(`Sending event to API: ${endpoint}`, payload);
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.sanderson_farms?.restnonce,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorResponse = await response.json();
        Logger.error(`Tracking failed for ${endpoint}:`, errorResponse);
        throw new Error(`Tracking failed for ${endpoint}`);
      }

      Logger.log(
        `Event sent to API successfully: ${endpoint}`,
        await response.json()
      );
    } catch (error) {
      Logger.error(`Error sending event to ${endpoint}`, error);
      throw error;
    }
  }
}

export default ServerTrackerManager;
